import React, { forwardRef } from 'react';
import './citation-print.scss';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import configurationStore from 'common/stores/configuration/configuration.store';
import ViolationLogo from 'assets/icons/violation.png';
import { TrafficLogVisitorType } from 'common/constans/common.constants';
import { CitationStatus } from 'features/eiq-manage/enums/citation-status.enum';
import LegalNotes from './legal-notes';
import { StrToDateTimeLocal } from 'common/helpers/date.helper';

interface ICitationPrintProps {
  citation: ICitationRead;
}

const CitationPrint = forwardRef(({ citation }: ICitationPrintProps, ref: any) => {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { communityName } = configurationStore.configuration!.default;
  const {
    sequenceNumber,
    ownerType,
    guestName,
    companyName,
    status,
    licensePlate,
    suspensionDays,
    residentName,
    externalId,
    latitude,
    longitude,
    speedLimit,
    violatingSpeed,
    occurredAt,
    photosUrl,
    totalPaymentAmount,
    paymentLinkUrl,
  } = citation;

  const getLegalNotes = (sequenceNumber: number) => {
    const rules = configurationStore.configuration?.citations.rules || [];

    if (rules.length === 0) {
      return '';
    }

    let index = sequenceNumber - 1;

    if (index === -1) {
      index = 0;
    }

    const result = index < rules.length ? rules[index] : rules[rules.length - 1];
    return result.legalNotes;
  };

  const getCitationCountText = () => {
    switch (sequenceNumber) {
      case 0:
        return '0';
      case 1:
        return '1st';
      case 2:
        return '2nd';
      case 3:
        return '3rd';
      default:
        return `${sequenceNumber}th`;
    }
  };

  const isGuest = () => {
    return ownerType !== TrafficLogVisitorType.Resident;
  };

  const getGuestName = () => {
    if (guestName) {
      let result = guestName;
      if (companyName) {
        result += ` (${companyName})`;
      }
      return result;
    }
    if (companyName) {
      return companyName;
    }
    return '';
  };

  const getRegisteredTo = () => {
    if (isGuest()) {
      return getGuestName();
    }
    return residentName;
  };

  const getMainText = () => {
    if (status === CitationStatus.Warning) {
      if (isGuest()) {
        return (
          <span>
            A visitor associated with your property was recorded speeding in the community using vehicle tag{' '}
            <span className="bold">{licensePlate}</span>. This is a formal warning, and further violations will result in additional
            penalties.
          </span>
        );
      } else {
        return (
          <span>
            We are writing to notify you that your vehicle with tag <span className="bold">{licensePlate}</span> was recorded speeding
            within the community. This is a formal warning, and further violations will result in additional penalties.
          </span>
        );
      }
    }

    if (isGuest()) {
      return (
        <span>
          A visitor associated with your property was recorded speeding in the community using vehicle tag{' '}
          <span className="bold">{licensePlate}</span>.
        </span>
      );
    } else {
      return (
        <span>
          Your vehicle with tag <span className="bold">{licensePlate}</span> was recorded speeding within the community.
        </span>
      );
    }
  };

  const getPayNowLink = () => {
    if (paymentLinkUrl && configurationStore.configuration?.citations.isAutoIssueInvoice) {
      return (
        <a href={paymentLinkUrl} target="_blank">
          (Pay Now)
        </a>
      );
    }

    return '';
  };

  const getPenaltyText = () => {
    if (status === CitationStatus.Warning) {
      return (
        <span>
          This is a <span className="italic">warning</span>. A second citation will result in a fine.
        </span>
      );
    }

    if (totalPaymentAmount && suspensionDays) {
      return (
        <span>
          ${totalPaymentAmount} fine with {suspensionDays} days transponder suspension {getPayNowLink()}
        </span>
      );
    }
    if (totalPaymentAmount) {
      return (
        <span>
          ${totalPaymentAmount} fine {getPayNowLink()}
        </span>
      );
    }
    return <span>{suspensionDays} days transponder suspension</span>;
  };

  return (
    <div className="citation-print">
      <div className="citation-print-body" ref={ref}>
        <div className="header">
          <div className="title">{communityName} Community</div>
          <div className="title-info">
            <div className="ticket">Ticket#: {externalId}</div>
            <div className="date">Date: {StrToDateTimeLocal(occurredAt)?.format('MM/DD/YYYY h:mmA')}</div>
          </div>
        </div>
        <div className="info">
          <div className="column violation1">
            <div>{getMainText()}</div>
            <div className="field">
              <span>
                <span className="bold">Citation Count: </span>
                <span className="orange bold">{getCitationCountText() + ' Citation' + (isGuest() ? ' (Visitor)' : '')}</span>{' '}
              </span>
              <br />
              <span>
                <span className="bold">Penalty: </span>
                <span>{getPenaltyText()}</span>
              </span>
            </div>
          </div>
          <div className="column">
            <div className="bold">INCIDENT DETAILS</div>
            <div className="violation-field">
              License Plate:
              <span className="bold"> {licensePlate}</span>
            </div>
            <div className="violation-field">
              Registered to:
              <span className="bold"> {getRegisteredTo()}</span>
            </div>
            <div className="violation-field">
              Speed Limit:
              <span className="bold green"> {speedLimit} mph</span>
            </div>
            <div className="violation-field">
              Recorded Speed:
              <span className="bold orange"> {violatingSpeed} mph</span>
            </div>
            <div className="violation-field">
              Date & Time:
              <span className="bold"> {StrToDateTimeLocal(occurredAt)?.format('MM/DD/YYYY h:mmA')}</span>
            </div>
          </div>
        </div>
        <div className="legal-notice">
          <LegalNotes text={getLegalNotes(sequenceNumber)} />
        </div>
        <div className="photos">
          <div className="header violation">CAPTURED EVIDENCE</div>
          <div className="images">
            {photosUrl.slice(0, 5).map((item, index) => {
              return <img key={`citation-img-${index}`} src={item} alt=""></img>;
            })}
          </div>
          <div className="sub-header violation">
            <div>Location of the Violation</div>
            <div>Closeup of the License Plate</div>
          </div>
          <div className="license-images violation">
            <div className="location">
              <img
                src={`https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=16&size=400x400&markers=color:red|label:S|${citation.latitude},${citation.longitude}&key=${googleMapsApiKey}`}
                alt=""></img>
            </div>

            <div className="img" style={{ borderLeft: '2px solid #000000' }}>
              {photosUrl.length && <img src={photosUrl.at(-1)} alt="violation img"></img>}
            </div>
          </div>
        </div>
        <div className="footer">
          <div className="violation-text">
            This citation is issued by the <span className="italic">{communityName} Homeowner’s Association (HOA)</span> as part of our
            ongoing efforts to maintain safety and compliance within our community. Speeding poses a significant risk to residents,
            particularly children and pedestrians.
          </div>
          <div className="violation-text">
            We appreciate your cooperation in maintaining a safe and secure community for all residents. Should you have any questions or
            concerns, please do not hesitate to reach out with the citation <span className="bold">#{externalId}</span>.
          </div>
          <div className="violation-text">
            <span className="bold">Sincerely,</span> <br />
            <span>{communityName} Homeowner’s Association</span>
          </div>
          <div className="violation-logo">
            <img src={ViolationLogo} alt="Violation Logo"></img>
          </div>
        </div>
      </div>
    </div>
  );
});

export default observer(CitationPrint);
